import { FilterPills } from '@aecom-ai-engineering/react-components';
import {
  useGetBlobContractorsQuery,
  useGetFindingsQuery,
  useGetFindingsUniqueBlobsQuery,
  useGetFindingsUniqueLabelsQuery,
  useDebouncedSearch,
} from '@consigli/hooks';
import { DocumentPackage, DocumentPackageType, Finding, ProcessorName } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { useEffect, useMemo, useState } from 'react';

import { Combobox, DataTable, DEFAULT_PAGE_SIZE, Pagination, Search } from '@/components';

import { FindingContent } from './finding-content';
import { findingsColumns } from './findings-columns';
import { FindingsFilterState } from './types';

const PROJECT_RELATED_BLOB_ID = '__projectrelated__';

const processorNameOptions = Object.values(ProcessorName).map((value) => ({
  label: formatEnumToReadableText(value),
  value,
}));

const ALL_CONTRACTORS_VALUE = '__all__';

interface FindingsTabProps {
  documentPackage: DocumentPackage;
  filters: FindingsFilterState;
  onFiltersChange: (filters: FindingsFilterState) => void;
}

/**
 * The findings view for a document package.
 * Displays a server-paginated DataTable of findings in the package, with filters
 * for processor, document, label, and free-text search.
 * For Bid Review packages, adds contractor pill selection to scope findings.
 */
export const FindingsTab = ({ documentPackage, filters, onFiltersChange }: FindingsTabProps) => {
  const projectId = documentPackage.project;
  const packageId = documentPackage.id;
  const isBidReview = documentPackage.type === DocumentPackageType.OFFER;

  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebouncedSearch(searchInput, 400);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFiltersChange({ ...filters, search: debouncedSearch });
    }
  }, [debouncedSearch, filters, onFiltersChange]);

  const { processorName, blobIds, labels, search, reference } = filters;

  const { data: contractors = [], isLoading: contractorsLoading } = useGetBlobContractorsQuery(
    { projectId, packageId },
    { skip: !isBidReview },
  );

  const isLegacyOfferPackage = isBidReview && !contractorsLoading && contractors.length === 0;

  const bidDocumentsOnly =
    isBidReview && !isLegacyOfferPackage && !reference && blobIds.length === 0 ? true : undefined;

  const pageSize = DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);

  const { data, isFetching } = useGetFindingsQuery({
    projectId,
    packageId,
    page,
    pageSize,
    processorName,
    blobIds: blobIds.length > 0 ? blobIds : undefined,
    labels: labels.length > 0 ? labels : undefined,
    search: search.trim() || undefined,
    reference,
    bidDocumentsOnly,
  });

  const findings = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  // A narrowed filter can shrink the result set below the current page, so
  // always return to the first page when the active filters change.
  useEffect(() => {
    setPage(1);
  }, [processorName, blobIds, labels, search, reference]);

  // Clamp to the last populated page if the total shrinks beneath it (e.g. a
  // finding was deleted elsewhere), so the user never lands on an empty page.
  useEffect(() => {
    if (isFetching) return;
    const maxPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [isFetching, totalCount, page, pageSize]);

  const { data: uniqueBlobs = [] } = useGetFindingsUniqueBlobsQuery({
    projectId,
    packageId,
    labels: labels.length > 0 ? labels : undefined,
  });

  const { data: uniqueLabels = [] } = useGetFindingsUniqueLabelsQuery({
    projectId,
    packageId,
    blobIds: blobIds.length > 0 ? blobIds : undefined,
  });

  const blobOptions = useMemo(
    () =>
      uniqueBlobs.map((blob) => ({
        label: blob.id === PROJECT_RELATED_BLOB_ID ? 'Project-related finding' : blob.name,
        value: blob.id,
      })),
    [uniqueBlobs],
  );

  const labelOptions = useMemo(
    () => uniqueLabels.map((label) => ({ label, value: label })),
    [uniqueLabels],
  );

  const handleProcessorChange = (values: string[]) => {
    const current = processorName ? [processorName] : [];
    const added = values.filter((v) => !current.includes(v as ProcessorName));
    onFiltersChange({
      ...filters,
      processorName: added.length > 0 ? (added[0] as ProcessorName) : undefined,
    });
  };

  const handleBlobIdsChange = (values: string[]) => {
    onFiltersChange({ ...filters, blobIds: values });
  };

  const handleLabelsChange = (values: string[]) => {
    onFiltersChange({ ...filters, labels: values });
  };

  const handleSelectContractor = (contractor: string | undefined) => {
    onFiltersChange({ ...filters, reference: contractor });
  };

  const contractorPillItems = useMemo(
    () => [
      { label: 'All', value: ALL_CONTRACTORS_VALUE },
      ...contractors.map((contractor) => ({ label: contractor, value: contractor })),
    ],
    [contractors],
  );

  return (
    <div className="w-full space-y-4">
      {isBidReview && contractors.length > 0 && (
        <FilterPills
          label="Filter by contractor"
          items={contractorPillItems}
          value={reference ? [reference] : [ALL_CONTRACTORS_VALUE]}
          onValueChange={({ value }) => {
            const selected = value[0];
            handleSelectContractor(selected === ALL_CONTRACTORS_VALUE ? undefined : selected);
          }}
        />
      )}
      <div className="flex gap-2 flex-wrap items-start">
        <Search
          text={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search findings..."
          className="w-[280px]"
        />
        <Combobox
          optionGroups={[{ label: 'Processors', options: processorNameOptions }]}
          value={processorName ? [processorName] : []}
          onValueChange={handleProcessorChange}
          placeholder="Filter by processor"
          fallbackLabel="No processors found"
          className="w-[240px]"
        />
        <Combobox
          optionGroups={[{ label: 'Documents', options: blobOptions }]}
          value={blobIds}
          onValueChange={handleBlobIdsChange}
          placeholder="Filter by document"
          fallbackLabel="No documents found"
          searchPlaceholder="Search documents..."
          className="w-[240px]"
        />
        <Combobox
          optionGroups={[{ label: 'Labels', options: labelOptions }]}
          value={labels}
          onValueChange={handleLabelsChange}
          placeholder="Filter by label"
          fallbackLabel="No labels found"
          searchPlaceholder="Search labels..."
          className="w-[240px]"
        />
      </div>
      <DataTable
        columns={findingsColumns}
        data={findings}
        isLoading={isFetching}
        expandOnRowClick
        expandedContent={(finding: Finding) => <FindingContent finding={finding} />}
        scrollable
        height="60vh"
      />
      <Pagination
        onPageChange={setPage}
        pageSize={pageSize}
        currentPage={page}
        totalCount={totalCount}
      />
    </div>
  );
};
